import { useHomeSectionsStore } from '~/stores/apostatudo/home-sections';
import { fetchHomeSectionsHandler } from '~/handlers/fetch-home-sections.handler';
import homeSectionsFallback from '~/assets/json/home-sections.fallback.json';

function applyHomeSectionToStore(
  store: ReturnType<typeof useHomeSectionsStore>,
  homeSection: HomeSectionData | HomeSection,
): boolean {
  if (homeSection && 'items' in homeSection) {
    store.setHomeSectionData(homeSection as HomeSectionData);

    return true;
  }

  if (homeSection && 'categories' in homeSection) {
    store.setHomeSection(homeSection as HomeSection);

    return true;
  }

  return false;
}

function applyFallback(store: ReturnType<typeof useHomeSectionsStore>): void {
  try {
    const fallback = (
      homeSectionsFallback as unknown as {
        data: HomeSectionData | HomeSection;
      }
    ).data;

    if (fallback) {
      applyHomeSectionToStore(store, fallback);
    }
  } catch (fallbackError) {
    console.error(
      '[useHomeSections] ❌ Falha ao aplicar fallback JSON:',
      fallbackError,
    );
  }
}

export const useHomeSections = () => {
  const store = useHomeSectionsStore();

  const fetchHomeSections = async () => {
    // Se já tem dados (nova ou antiga API), não refaz requisição
    if (store.hasCategories || store.hasItems) return;

    store.loading = true;
    store.error = null;

    try {
      const { homeSection } = await fetchHomeSectionsHandler();

      if (!homeSection) {
        throw new Error('API returned null homeSection');
      }

      const applied = applyHomeSectionToStore(store, homeSection);

      if (!applied) {
        console.warn(
          '[useHomeSections] ⚠️ Estrutura desconhecida, aplicando fallback:',
          homeSection,
        );
        applyFallback(store);
      }
    } catch (error) {
      console.error('[useHomeSections] ❌ Erro ao buscar dados:', error);
      store.error = error instanceof Error ? error.message : 'Unknown error';

      // Garante que a home sempre renderiza, mesmo se o handler falhar
      // (ex.: erro ao parsear JSON, módulo dinâmico falhando no Safari iOS).
      if (!store.hasItems && !store.hasCategories) {
        applyFallback(store);
      }
    } finally {
      store.loading = false;
    }
  };

  return {
    fetchHomeSections,

    // Nova API
    validItems: computed(() => store.validItems),
    sortedItems: computed(() => store.sortedItems),
    itemsByType: computed(() => store.itemsByType),
    hasItems: computed(() => store.hasItems),
    isUsingNewApi: computed(() => store.isUsingNewApi),

    // Antiga API (compatibilidade)
    categories: computed(() => store.categories),
    hasCategories: computed(() => store.hasCategories),

    // Compartilhado
    loading: computed(() => store.loading),
    error: computed(() => store.error),
    isApiWorking: computed(() => store.isApiWorking),
  };
};
